// Contact.jsx
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import styles from "./Contact.module.css";

// Set these in .env (see .env.example)
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const emptyForm = { name: "", email: "", topic: "Consulting", message: "" };

const Contact = () => {
  const location = useLocation();
  // The savings calculator can hand over a topic and a prefilled message
  const [formData, setFormData] = useState({ ...emptyForm, ...location.state });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      // Not configured: fall back to the visitor's mail app so the message isn't lost
      const subject = encodeURIComponent(`[${formData.topic}] Message from ${formData.name}`);
      const body = encodeURIComponent(`${formData.message}\n\n${formData.name} <${formData.email}>`);
      window.location.href = `mailto:info@myapple.gr?subject=${subject}&body=${body}`;
      return;
    }

    setStatus("sending");
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          reply_to: formData.email,
          topic: formData.topic,
          message: formData.message,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      setStatus("sent");
      setFormData(emptyForm);
      window.gtag?.("event", "generate_lead", { topic: formData.topic });
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
    }
  };

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h2 className={styles.title}>Contact Me</h2>
      <p className={styles.subtitle}>
        Let’s connect and discuss how we can help your business succeed.
      </p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          type="text"
          name="name"
          placeholder="Your Name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Your Email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <select
          name="topic"
          value={formData.topic}
          onChange={handleChange}
          required
        >
          <option value="Consulting">Consulting</option>
          <option value="Data Analysis">Data Analysis</option>
          <option value="Transport Optimization">Transport Optimization</option>
          <option value="Web Development">Web Development</option>
          <option value="Other">Other</option>
        </select>

        <textarea
          name="message"
          rows="5"
          placeholder="Your message..."
          value={formData.message}
          onChange={handleChange}
          required
        />

        <button type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Sending..." : "Send Message"}
        </button>

        {status === "sent" && (
          <p className={styles.success} role="status">
            Thank you! Your message has been sent. I'll get back to you soon.
          </p>
        )}
        {status === "error" && (
          <p className={styles.error} role="alert">
            Something went wrong. Please email me directly at info@myapple.gr.
          </p>
        )}
      </form>

      <div className={styles.altContact}>
        <p>📧 You can also email me directly:</p>
        <a href="mailto:info@myapple.gr">info@myapple.gr</a>
      </div>
    </motion.div>
  );
};

export default Contact;